import { createGeminiImage, isGeminiAvailable } from './gemini-client.js';

const form = document.querySelector('form[data-daylife-form]');
const breedSelect = document.querySelector('#daylife-breed');
const themeSelect = document.querySelector('#daylife-theme');
const notesInput = document.querySelector('#daylife-notes');
const statusEl = document.querySelector('[data-status]');
const previewFrame = document.querySelector('[data-daylife-preview]');
const downloadBtn = document.querySelector('[data-download]');
const resetBtn = document.querySelector('[data-reset]');
const submitBtn = form?.querySelector('button[type="submit"]');

let geminiReady = true;
const DEFAULT_STATUS = 'Select a breed and theme to get started.';

const BREED_TRAITS = {
  netherland: 'Netherland Dwarf proportions—petite face, bright marble eyes, upright ears, rounded cheeks.',
  lop: 'Mini Lop physique—plush fur, floppy ears framing the cheeks, gentle expression.',
  lionhead: 'Lionhead fluff—halo mane framing curious eyes and tidy whiskers.',
  rex: 'Mini Rex texture—velvet fur, soft matte shading, button nose.',
  angora: 'English Angora fiber—tufted ears, fluffy cheeks, hand-spun wool texture.'
};

const THEME_SCENES = {
  atelier: [
    'Sunrise stretch: rabbit warms up in a tatami atelier with sketchbooks and golden window light.',
    'Late morning ritual: brewing kinako latte beside stacks of art zines.',
    'Afternoon gallery: strolling and studying framed croissant silhouettes.',
    'Moonlit studio: hanging mini canvases and tidying paint brushes before sleep.'
  ],
  garden: [
    'Sunlit garden: tending herbs with dew on clover leaves.',
    'Midday shade: lounging in a greenhouse hammock amid terracotta pots.',
    'Dusk harvest: gathering carrots and shiso into a woven basket.',
    'Night glow: sipping tea under string lights with gentle fireflies.'
  ],
  city: [
    'Dawn commute: waiting for a vintage tram with a tiny satchel.',
    'Cafe notebook: sketching latte art at a terrazzo table.',
    'Afternoon errands: browsing a stationery market with neon signage.',
    'Night skyline: watching city lights from a rooftop garden.'
  ],
  festival: [
    'Morning crafting: cutting lantern paper and tying ribbons.',
    'Midday rehearsal: practicing taiko drum beats beside festival stalls.',
    'Sunset parade: hopping through lantern arches with confetti.',
    'Night fireworks: cozy scarf, sparkling sky, holding sweet dango.'
  ]
};

function buildPrompt(breedKey, themeKey, notes = '') {
  const breedPrompt = BREED_TRAITS[breedKey] || BREED_TRAITS.netherland;
  const scenes = THEME_SCENES[themeKey] || THEME_SCENES.atelier;
  const additions = notes.trim();
  const sceneText = scenes.map((line, index) => `Panel ${index + 1}: ${line}`).join(' ');
  return `Create a single square illustration divided into four equal panels (2x2 grid) with thin cream dividers. ${breedPrompt} Style should be Croissant Bunny cartoon mixed with gouache texture, soft outlines, and small serif labels for each panel (morning, midday, afternoon, night). ${sceneText} Keep the color palette cohesive, emphasize cozy props, and leave a subtle paper-grain background.${additions ? ` Extra direction: ${additions}.` : ''}`;
}

function setStatus(message, isError = false) {
  if (!statusEl) return;
  statusEl.textContent = message;
  statusEl.classList.toggle('error', Boolean(isError));
}

function setPreview(base64) {
  if (!previewFrame) return;
  if (!base64) {
    previewFrame.style.backgroundImage = '';
    previewFrame.classList.remove('has-image');
    previewFrame.classList.add('placeholder');
    if (downloadBtn) {
      downloadBtn.href = 'javascript:void(0)';
      downloadBtn.classList.add('is-hidden');
      downloadBtn.setAttribute('aria-disabled', 'true');
      downloadBtn.setAttribute('tabindex', '-1');
    }
    return;
  }

  const dataUrl = `data:image/png;base64,${base64}`;
  previewFrame.style.backgroundImage = `url(${dataUrl})`;
  previewFrame.classList.add('has-image');
  previewFrame.classList.remove('placeholder');
  if (downloadBtn) {
    downloadBtn.href = dataUrl;
    downloadBtn.classList.remove('is-hidden');
    downloadBtn.removeAttribute('aria-disabled');
    downloadBtn.removeAttribute('tabindex');
  }
}

async function initGeminiAvailability() {
  if (!form) return;
  geminiReady = await isGeminiAvailable();
  if (!geminiReady) {
    submitBtn?.setAttribute('disabled', 'true');
    form.classList.add('tool-disabled');
    setStatus('Image generation is offline. Please visit again soon.');
  }
}

function handleReset() {
  form?.reset();
  setPreview('');
  setStatus(DEFAULT_STATUS);
}

if (form && breedSelect && themeSelect && previewFrame && statusEl) {
  handleReset();

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!geminiReady) {
      setStatus('Image generation is offline. Try again later.', true);
      return;
    }

    const breed = breedSelect.value;
    const theme = themeSelect.value;
    const notes = notesInput?.value || '';
    const prompt = buildPrompt(breed, theme, notes);

    try {
      setStatus('Composing your four-panel story…');
      setPreview('');
      const output = await createGeminiImage({ prompt });
      setPreview(output);
      setStatus('Grid ready! Download the PNG or adjust the controls.');
    } catch (error) {
      console.error(error);
      setStatus(error.message || 'Generation failed. Please try again.', true);
      setPreview('');
    }
  });

  resetBtn?.addEventListener('click', handleReset);
  initGeminiAvailability();
}
